/**
 * Reference flow: drive a follow-up question step after a dashboard is shown.
 *
 * This is the one canonical example of the turn-flow engine reaching the full
 * envelope from a single declarative definition. Once {@link SHOW_DASHBOARD}
 * has settled in the transcript, the flow rests in its `followUp` state and
 * emits three composed transforms for the next turn:
 *
 *  - {@link forceTool} pins the model to {@link ASK_USER_QUESTION}.
 *  - {@link appendMessages} adds one ephemeral system message framing the kind
 *    of follow-up to ask (it lives only in this turn's request, never the
 *    persisted transcript).
 *  - {@link withCallSettings} lowers the temperature for that focused step.
 *
 * Once `askUserQuestion` itself settles the flow reaches its terminal state and
 * votes `'stop'`, ending the agent loop.
 *
 * The state ids use the real wire tool names (`showDashboard`,
 * `askUserQuestion`) — the same strings the transcript carries as `tool-<name>`
 * parts — so derivation keys off actual tool activity.
 */

import { flow, next, terminal } from './flow.ts';
import { appendMessages, forceTool, withCallSettings } from './transforms.ts';
import type { FlowResolverEntry } from './types.ts';

/** Wire name of the dashboard-presentation tool. */
export const SHOW_DASHBOARD = 'showDashboard';

/** Wire name of the follow-up question tool. */
export const ASK_USER_QUESTION = 'askUserQuestion';

/** Lowered temperature for the focused follow-up step. */
export const FOLLOW_UP_TEMPERATURE = 0.2;

/** Ephemeral system message framing the follow-up the model should ask. */
export const FOLLOW_UP_FRAMING =
  'A dashboard was just presented. Ask the user one focused follow-up question to refine or extend it.';

/**
 * The reference {@link FlowResolverEntry}. Register it with `useRegisterFlow`
 * (referentially stable — it is a module-level constant) to steer every turn
 * after a dashboard is shown toward a single framed follow-up question.
 */
export const followUpFlow: FlowResolverEntry = flow('follow-up', {
  [SHOW_DASHBOARD]: {
    tool: SHOW_DASHBOARD,
    enter: [],
    transition: next(ASK_USER_QUESTION),
  },
  [ASK_USER_QUESTION]: {
    tool: ASK_USER_QUESTION,
    enter: [
      forceTool(ASK_USER_QUESTION),
      appendMessages([{ role: 'system', content: FOLLOW_UP_FRAMING }]),
      withCallSettings({ temperature: FOLLOW_UP_TEMPERATURE }),
    ],
    transition: terminal(),
  },
});
